import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Icon } from '@iconify/react';
import kkpService from '../../services/kkpService';
import { BRIEF_ACCEPTED_FILE_TYPES, formatBriefFileSize } from '../../utils/kkpBriefFichierUtils';
import { AppButton } from '../ui';
import useAppConfirm from '../../hooks/useAppConfirm';
import '../../styles/kkp-brief-fichiers.scss';

function fileIcon(mime = '') {
  if (mime.startsWith('image/')) return 'mdi:file-image-outline';
  if (mime.startsWith('video/')) return 'mdi:file-video-outline';
  if (mime === 'application/pdf') return 'mdi:file-pdf-box';
  if (mime.startsWith('text/')) return 'mdi:file-document-outline';
  return 'mdi:paperclip';
}

const KkpBriefAttachmentsEditor = ({
  venteId,
  piecesJointes = [],
  onChange,
  onError,
  disabled = false,
}) => {
  const inputRef = useRef(null);
  const [items, setItems] = useState(piecesJointes);
  const [uploading, setUploading] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const { ask, modal } = useAppConfirm();

  useEffect(() => {
    setItems(piecesJointes);
  }, [piecesJointes, venteId]);

  const syncItems = useCallback((nextItems) => {
    setItems(nextItems);
    onChange?.(nextItems);
  }, [onChange]);

  const handleUpload = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file || !venteId) return;

    setUploading(true);
    try {
      const uploaded = await kkpService.uploadBriefFichier(venteId, file);
      syncItems([...items, uploaded]);
    } catch (err) {
      onError?.(err.message);
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (fileId) => {
    if (!venteId) return;
    const ok = await ask({
      title: 'Supprimer cette pièce jointe ?',
      message: 'Le fichier sera retiré du brief.',
      confirmLabel: 'Supprimer',
      confirmVariant: 'danger',
    });
    if (!ok) return;

    setDeletingId(fileId);
    try {
      await kkpService.deleteBriefFichier(venteId, fileId);
      syncItems(items.filter((item) => item.id !== fileId));
    } catch (err) {
      onError?.(err.message);
    } finally {
      setDeletingId(null);
    }
  };

  if (!venteId) {
    return (
      <div className="kkp-brief-fichiers kkp-brief-fichiers--disabled mt-3">
        <p className="text-secondary-light text-sm mb-0">
          Enregistrez la vente pour pouvoir ajouter des pièces jointes au brief.
        </p>
      </div>
    );
  }

  return (
    <>
    <div className="kkp-brief-fichiers mt-3">
      <div className="d-flex align-items-center justify-content-between gap-2 mb-2">
        <label className="form-label fw-medium mb-0">Pièces jointes</label>
        <AppButton
          type="button"
          size="sm"
          variant="outline-primary"
          onClick={() => inputRef.current?.click()}
          disabled={disabled}
          loading={uploading}
          loadingLabel="Envoi…"
          icon="mdi:paperclip"
        >
          Ajouter un fichier
        </AppButton>
        <input
          ref={inputRef}
          type="file"
          className="d-none"
          accept={BRIEF_ACCEPTED_FILE_TYPES}
          onChange={handleUpload}
          disabled={disabled || uploading}
        />
      </div>

      <p className="text-secondary-light text-sm mb-2">
        Images, vidéos, PDF, documents texte… (max 1 Go par fichier)
      </p>

      {items.length === 0 ? (
        <div className="kkp-brief-fichiers__empty text-secondary-light text-sm">
          Aucune pièce jointe pour le moment.
        </div>
      ) : (
        <ul className="kkp-brief-fichiers__list list-unstyled mb-0">
          {items.map((item) => (
            <li key={item.id} className="kkp-brief-fichiers__item">
              <Icon icon={fileIcon(item.mime)} className="kkp-brief-fichiers__icon" />
              <div className="kkp-brief-fichiers__meta">
                <span className="kkp-brief-fichiers__name">{item.nom}</span>
                <span className="kkp-brief-fichiers__size">{formatBriefFileSize(item.taille)}</span>
              </div>
              <AppButton
                type="button"
                size="sm"
                variant="outline-danger"
                title="Supprimer"
                loading={deletingId === item.id}
                icon="mdi:delete-outline"
                onClick={() => handleDelete(item.id)}
              />
            </li>
          ))}
        </ul>
      )}
    </div>
    {modal}
    </>
  );
};

export default KkpBriefAttachmentsEditor;
